import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Usuario {
  id: string;
  user_id: string;
  nombre: string;
  email: string;
  rol: string;
  roles_asignados?: { role: string }[];
}

interface EditarUsuarioModalProps {
  usuario: Usuario | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUsuarioActualizado: () => void;
}

const ROLES_DISPONIBLES = [
  { value: 'admin', label: 'Administrador', description: 'Acceso total al sistema' },
  { value: 'moderator', label: 'Moderador', description: 'Puede gestionar contenido' },
  { value: 'user', label: 'Usuario', description: 'Acceso básico' },
];

export const EditarUsuarioModal = ({ usuario, open, onOpenChange, onUsuarioActualizado }: EditarUsuarioModalProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [nombre, setNombre] = useState("");
  const [rolesSeleccionados, setRolesSeleccionados] = useState<string[]>([]);

  useEffect(() => {
    if (usuario) {
      setNombre(usuario.nombre || "");
      setRolesSeleccionados(usuario.roles_asignados?.map(r => r.role) || []);
    }
  }, [usuario]);

  const handleToggleRol = (rol: string) => {
    setRolesSeleccionados(prev =>
      prev.includes(rol)
        ? prev.filter(r => r !== rol)
        : [...prev, rol]
    );
  };

  const handleGuardar = async () => {
    if (!usuario) return;

    setLoading(true);
    try {
      // Actualizar nombre en profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ nombre })
        .eq('user_id', usuario.user_id);

      if (profileError) throw profileError;

      // Eliminar roles actuales
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', usuario.user_id);

      if (deleteError) throw deleteError;

      // Insertar nuevos roles
      if (rolesSeleccionados.length > 0) {
        const rolesData = rolesSeleccionados.map(role => ({
          user_id: usuario.user_id,
          role: role as 'admin' | 'moderator' | 'user'
        }));

        const { error: insertError } = await supabase
          .from('user_roles')
          .insert(rolesData);

        if (insertError) throw insertError;
      }

      toast({
        title: "Usuario actualizado",
        description: "Los cambios se han guardado correctamente",
      });

      onOpenChange(false);
      onUsuarioActualizado();
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el usuario",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Usuario</DialogTitle>
          <DialogDescription>
            Modifica la información y roles del usuario
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={usuario?.email || ""}
              disabled
              className="bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre</Label>
            <Input
              id="nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre del usuario"
            />
          </div>

          <div className="space-y-3">
            <Label>Roles Asignados</Label>
            {ROLES_DISPONIBLES.map((rol) => (
              <div key={rol.value} className="flex items-start space-x-3 p-3 border rounded-lg">
                <Checkbox
                  id={rol.value}
                  checked={rolesSeleccionados.includes(rol.value)}
                  onCheckedChange={() => handleToggleRol(rol.value)}
                />
                <div className="space-y-1 flex-1">
                  <Label
                    htmlFor={rol.value}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {rol.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {rol.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={loading}>
            {loading ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
